import { type FC } from 'preact/compat';

import cn from 'clsx';

import { useIntl } from '@/app/lib/localization/hooks/use-intl';

import { useCirclesScoreModal } from '@/entities/playdeck/models/playdeck.store';

import { type CirclesGameState, type CirclesMedal } from '@/shared/api/circles/types';

import { NoAdPurchase } from '@/widget/no-ad-purchase/ui';

import avatarPlaceholder from '../assets/avatar-placeholder.svg';
import cupImage from '../assets/cup.png';

import s from './index.module.scss';

const MEDAL_EMOJI: Record<CirclesMedal, string> = {
  bronze: '🥉',
  silver: '🥈',
  gold: '🥇',
};

const MEDAL_LABEL_KEY: Record<CirclesMedal, string> = {
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
};

const MEDAL_LABEL_CLASS: Record<CirclesMedal, string> = {
  bronze: s.medalLabelBronze,
  silver: s.medalLabelSilver,
  gold: s.medalLabelGold,
};

const MEDAL_ORDER: CirclesMedal[] = ['bronze', 'silver', 'gold'];

/** Возвращает thresholds, отсортированные в порядке bronze→silver→gold. */
const sortThresholds = (data: CirclesGameState) => {
  const list = data.thresholds ?? [];
  return MEDAL_ORDER.map((medal) => list.find((t) => t.medal === medal)).filter(
    (t): t is CirclesGameState['thresholds'][number] => Boolean(t)
  );
};

/** Положение ползунка по шкале bronze→gold (0..100). */
const computeProgressPercent = (
  score: number,
  thresholds: CirclesGameState['thresholds']
): number => {
  if (thresholds.length === 0) return 0;
  const first = thresholds[0];
  const last = thresholds[thresholds.length - 1];
  if (score <= first.score) return 0;
  if (score >= last.score) return 100;
  const stepSize = 100 / (thresholds.length - 1);
  for (let i = 0; i < thresholds.length - 1; i++) {
    const a = thresholds[i];
    const b = thresholds[i + 1];
    if (score >= a.score && score <= b.score) {
      const localRatio = (score - a.score) / (b.score - a.score);
      return i * stepSize + localRatio * stepSize;
    }
  }
  return 0;
};

/** Текущая достигнутая медаль. */
const currentMedal = (
  score: number,
  thresholds: CirclesGameState['thresholds']
): CirclesMedal | null => {
  let result: CirclesMedal | null = null;
  for (const t of thresholds) {
    if (score >= t.score) result = t.medal;
  }
  return result;
};

type RankBadgeColor = 'gold' | 'silver' | 'bronze';

const RANK_BADGE_BY_INDEX: RankBadgeColor[] = ['gold', 'silver', 'bronze'];

const RANK_BADGE_CLASS: Record<RankBadgeColor, string> = {
  gold: s.rankBadgeGold,
  silver: s.rankBadgeSilver,
  bronze: s.rankBadgeBronze,
};

interface LeaderRowProps {
  rank: number | null;
  rankBadgeColor?: RankBadgeColor | null;
  name: string;
  circleTitle: string | null;
  avatarUrl?: string | null;
  score: number | null;
  isSelf?: boolean;
  showTopPercent?: boolean;
  topPercent?: number | null;
  youLabel: string;
  scoreLabel: string;
}

const LeaderRow: FC<LeaderRowProps> = ({
  rank,
  rankBadgeColor,
  name,
  circleTitle,
  avatarUrl,
  score,
  isSelf,
  showTopPercent,
  topPercent,
  youLabel,
  scoreLabel,
}) => (
  <div className={cn(s.leaderRow, isSelf && s.leaderRowSelf)}>
    {isSelf && <div className={s.leaderRowSelfGlow} />}
    <div className={s.rankCell}>
      {rankBadgeColor ? (
        <span className={cn(s.rankBadge, RANK_BADGE_CLASS[rankBadgeColor])}>
          {rank ?? '—'}
        </span>
      ) : (
        <span className={s.rankNumber}>{rank ?? '—'}</span>
      )}
    </div>
    <img
      className={s.avatar}
      src={avatarUrl || avatarPlaceholder}
      alt=""
      onError={(e) => {
        (e.currentTarget as HTMLImageElement).src = avatarPlaceholder;
      }}
    />
    <div className={s.leaderInfo}>
      <span className={s.leaderName}>
        {name}
        {isSelf && <span className={s.youBadge}>{youLabel}</span>}
      </span>
      {circleTitle && <span className={s.leaderCircle}>{circleTitle}</span>}
    </div>
    <div className={s.leaderRight}>
      {showTopPercent && topPercent !== null && topPercent !== undefined && (
        <div className={s.topPercent}>
          <span>TOP</span>
          <span>{topPercent}%</span>
        </div>
      )}
      <div className={s.leaderScoreCol}>
        <span className={s.leaderScoreLabel}>{scoreLabel}</span>
        <span className={s.leaderScoreValue}>{score ?? 0}</span>
      </div>
    </div>
  </div>
);

interface RenderedLeaderRow {
  key: string;
  rank: number | null;
  badge: RankBadgeColor | null;
  name: string;
  circleTitle: string | null;
  avatarUrl: string | null;
  score: number | null;
  isSelf: boolean;
  showTopPercent: boolean;
}

const buildLeaderboardRows = (
  top3: CirclesGameState['leaderboard_top3'],
  user: CirclesGameState['user']
): RenderedLeaderRow[] => {
  const userRank = user.rank;
  const userInTop3 = userRank !== null && userRank >= 1 && userRank <= 3;

  // Юзер один (top3 пустой) → показываем только его строку как rank 1 / gold.
  if (top3.length === 0) {
    return [
      {
        key: 'self',
        rank: 1,
        badge: 'gold',
        name: user.name,
        circleTitle: user.circle_title,
        avatarUrl: user.avatar_url,
        score: user.best_score_period,
        isSelf: true,
        showTopPercent: false,
      },
    ];
  }

  // Юзер внутри top3 → рендерим top3, отмечаем self по индексу.
  if (userInTop3) {
    return top3.map((entry, idx) => ({
      key: `top-${idx}`,
      rank: idx + 1,
      badge: RANK_BADGE_BY_INDEX[idx] ?? null,
      name: entry.name,
      circleTitle: entry.circle_title,
      avatarUrl: entry.avatar_url,
      score: entry.score,
      isSelf: idx + 1 === userRank,
      showTopPercent: false,
    }));
  }

  // Юзер вне top3 → top2 + self строкой, TOP% показываем.
  const top2: RenderedLeaderRow[] = top3.slice(0, 2).map((entry, idx) => ({
    key: `top-${idx}`,
    rank: idx + 1,
    badge: RANK_BADGE_BY_INDEX[idx] ?? null,
    name: entry.name,
    circleTitle: entry.circle_title,
    avatarUrl: entry.avatar_url,
    score: entry.score,
    isSelf: false,
    showTopPercent: false,
  }));

  return [
    ...top2,
    {
      key: 'self',
      rank: userRank,
      badge: null,
      name: user.name,
      circleTitle: user.circle_title,
      avatarUrl: user.avatar_url,
      score: user.best_score_period,
      isSelf: true,
      showTopPercent: true,
    },
  ];
};

export const CirclesScoreModal: FC = () => {
  const t = useIntl();
  const modal = useCirclesScoreModal();

  if (!modal) return null;

  if (modal.status === 'loading') {
    return (
      <div className={cn(s.overlay, s.loadingOverlay)}>
        <div className={s.spinner} />
      </div>
    );
  }

  const { score, data } = modal;
  const { user } = data;
  const leaderboardTop3 = data.leaderboard_top3 ?? [];
  const thresholds = sortThresholds(data);
  const previousBest = user.previous_best_score ?? 0;
  const isRecord = score > previousBest;
  const progressPercent = computeProgressPercent(score, thresholds);
  const activeMedal = currentMedal(score, thresholds);
  const leaderboardRows = buildLeaderboardRows(leaderboardTop3, user);

  return (
    <div className={s.overlay}>
      <div className={s.content}>
        <div className={s.titleWrap}>
          {isRecord && (
            <>
              <img className={s.cupImage} src={cupImage} alt="" />
              {/* декоративные точки-блёстки */}
              <span
                className={s.sparkle}
                style={{ top: 26, left: 66, width: 2, height: 2 }}
              />
              <span
                className={s.sparkle}
                style={{ top: 28, left: 107, width: 2, height: 2 }}
              />
              <span
                className={s.sparkle}
                style={{ top: 8, left: 127, width: 3, height: 3 }}
              />
              <span
                className={s.sparkle}
                style={{ top: 46, left: 331, width: 3, height: 3 }}
              />
              <span
                className={s.sparkle}
                style={{ top: 34, left: 353, width: 2, height: 2 }}
              />
            </>
          )}
          <h1 className={s.title}>{t(isRecord ? 'New record' : 'Results')}</h1>
        </div>

        {/* Score-плашка */}
        <div className={cn(s.plate, s.scorePlate)}>
          <div className={s.scoreCol}>
            <span className={s.scoreLabel}>
              {t(isRecord ? 'New record' : 'Current result')}
            </span>
            <span className={cn(s.scoreValue, isRecord && s.scoreValueRecord)}>
              {score.toLocaleString('ru-RU')}
            </span>
          </div>
          <div className={s.scoreDivider} />
          <div className={s.scoreCol}>
            <span className={s.scoreLabel}>
              {t(isRecord ? 'Previous record' : 'Maximum result')}
            </span>
            <span className={s.scoreValue}>{previousBest.toLocaleString('ru-RU')}</span>
          </div>
        </div>

        {/* Medals-плашка */}
        <div className={cn(s.plate, s.medalsPlate)}>
          <div className={s.medalsHeader}>
            {activeMedal ? t(`${MEDAL_LABEL_KEY[activeMedal]} medal`) : t('Bronze medal')}
          </div>

          <div className={s.medalsRow}>
            {thresholds.map((th) => (
              <div key={th.medal} className={s.medalCol}>
                <div className={s.medalIconBox}>{MEDAL_EMOJI[th.medal]}</div>
              </div>
            ))}
          </div>

          <div className={s.progressTrack}>
            <div className={s.progressFill} style={{ width: `${progressPercent}%` }} />
            {thresholds.map((th, idx) => {
              const positionPercent =
                thresholds.length <= 1 ? 0 : (idx / (thresholds.length - 1)) * 100;
              const isPassed = score >= th.score;
              return (
                <div
                  key={th.medal}
                  className={cn(
                    s.progressSeparator,
                    isPassed && s.progressSeparatorActive
                  )}
                  style={{ left: `${positionPercent}%` }}
                />
              );
            })}
          </div>

          <div className={s.medalLabels}>
            {thresholds.map((th) => (
              <div key={th.medal} className={s.medalLabelCell}>
                <span className={MEDAL_LABEL_CLASS[th.medal]}>
                  {t(MEDAL_LABEL_KEY[th.medal])}
                </span>
                <span className={s.medalLabelValue}>
                  {th.score.toLocaleString('ru-RU')}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Leaderboard-плашка */}
        <div className={cn(s.plate, s.leaderboardPlate)}>
          {leaderboardRows.map((row) => (
            <LeaderRow
              key={row.key}
              rank={row.rank}
              rankBadgeColor={row.badge}
              name={row.name}
              circleTitle={row.circleTitle}
              avatarUrl={row.avatarUrl}
              score={row.score}
              isSelf={row.isSelf}
              showTopPercent={row.showTopPercent}
              topPercent={user.top_percent}
              youLabel={t('You')}
              scoreLabel={t('Score points label')}
            />
          ))}
        </div>

        <NoAdPurchase />
      </div>

      <button className={s.replayButton} onClick={modal.resolve}>
        {t('Play again')}
      </button>
    </div>
  );
};
